import { ArrowRightCircle, Eye, LoaderCircle, Play } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { api } from "@/api";
import { ConfirmDialog } from "@/components/ConfirmDialog";

export const AutoAdvanceEditor = ({ form, setForm, steps }) => (
  <div className="advance-editor" data-testid="auto-advance-editor">
    <label className="advance-toggle">
      <input type="checkbox" checked={Boolean(form.auto_advance_enabled)} onChange={(e) => setForm({ ...form, auto_advance_enabled: e.target.checked })} data-testid="auto-advance-enabled" />
      <ArrowRightCircle size={14} /> Passage automatique à l’étape suivante sans réponse
    </label>
    <div className={`advance-fields${form.auto_advance_enabled ? "" : " is-muted"}`}>
      <span>Après</span>
      <input type="number" min="1" max="60" value={form.auto_advance_days} onChange={(e) => setForm({ ...form, auto_advance_days: e.target.value })} data-testid="auto-advance-days" />
      <span>jour(s) ouvré(s) après l’envoi de l’e-mail, déplacer vers</span>
      <select
        value={form.auto_advance_step_id || ""}
        onChange={(e) => {
          const step = steps.find((item) => String(item.id) === e.target.value);
          setForm({ ...form, auto_advance_step_id: e.target.value ? Number(e.target.value) : null, auto_advance_step_name: step ? step.name : "" });
        }}
        data-testid="auto-advance-step"
      >
        <option value="">— choisir l’étape —</option>
        {steps.filter((step) => String(step.id) !== String(form.trigger_step_id)).map((step) => <option key={step.id} value={step.id}>{step.name}</option>)}
      </select>
    </div>
    <p className="field-help">Une fiche n’est déplacée que si elle est toujours dans l’étape, encore « à faire » dans noCRM, et qu’aucune réponse e-mail de la mairie n’a été reçue depuis l’envoi. Le passage s’exécute automatiquement plusieurs fois par jour, en semaine entre 8h et 19h (heure de Paris) — ou immédiatement via « Lancer maintenant ».</p>
  </div>
);

export const AutoAdvanceRow = ({ rule, onDone }) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirm, setConfirm] = useState(false);

  if (!rule.auto_advance_step_id) return null;

  const loadPreview = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/automations/${rule.id}/advance-preview`, { timeout: 120000 });
      setPreview(data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Aperçu impossible.");
    } finally {
      setLoading(false);
    }
  };

  const runNow = async () => {
    try {
      const { data } = await api.post(`/automations/${rule.id}/advance-run`, {}, { timeout: 120000 });
      toast.success(data.message);
      setPreview(null);
      onDone?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Lancement impossible.");
    } finally {
      setConfirm(false);
    }
  };

  return (
    <div className="advance-row" data-testid={`advance-row-${rule.id}`}>
      <span className={`advance-pill${rule.auto_advance_enabled ? "" : " is-off"}`} data-testid={`advance-summary-${rule.id}`}>
        <ArrowRightCircle size={13} /> {rule.auto_advance_enabled ? "Passage auto" : "Passage auto désactivé"} : après {rule.auto_advance_days} j ouvré(s) → <strong>{rule.auto_advance_step_name}</strong>
        {rule.auto_advance_last_run ? ` · dernier passage ${new Date(rule.auto_advance_last_run).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })} (${rule.auto_advance_last_moved || 0} fiche(s))` : ""}
      </span>
      <button type="button" className="link-button" onClick={loadPreview} disabled={loading} data-testid={`advance-preview-${rule.id}`}>
        {loading ? <LoaderCircle size={12} className="spinning" /> : <Eye size={12} />} Aperçu
      </button>
      {preview && (
        <span className="advance-preview" data-testid={`advance-preview-result-${rule.id}`}>
          <strong>{preview.count}</strong> fiche(s) seraient déplacées de « {preview.from_step} » vers « {preview.to_step} »
          {preview.count > 0 && (
            <button type="button" className="link-button" onClick={() => setConfirm(true)} data-testid={`advance-run-${rule.id}`}><Play size={12} /> Lancer maintenant</button>
          )}
        </span>
      )}
      <ConfirmDialog
        open={confirm}
        title={`Déplacer ${preview?.count || 0} fiche(s) vers « ${rule.auto_advance_step_name} » maintenant ?`}
        description="Les fiches seront déplacées dans noCRM immédiatement ; les e-mails de l’étape suivante partiront via la file d’envoi lissée."
        confirmLabel="Déplacer"
        onConfirm={runNow}
        onCancel={() => setConfirm(false)}
      />
    </div>
  );
};
